import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import SubHero from '../other/SubHero';

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0rem 2rem;
`;

const Section = styled.section<{ isVisible: boolean }>`
  margin-bottom: 3rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.7s ease,
    transform 0.8s ease;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
`;

// const SectionTitle = styled.h2`
//   font-size: 1.5rem;
//   font-weight: 300;
//   margin-bottom: 1.5rem;
//   font-family: 'Garamond', serif;
//   color: #13182a;
//   position: relative;
//   display: inline-block;
//   &:after {
//     content: '';
//     position: absolute;
//     width: 0;
//     height: 2px;
//     bottom: -2px;
//     left: 0;
//     background-color: #13182a;
//     transition: width 0.3s ease;
//     height: 1.5px;
//   }
//   &:hover:after {
//     width: 100%;
//   }
// `;

// const Paragraph = styled.p`
//   font-size: 1.125rem;
//   line-height: 1.8;
//   margin-bottom: 1.5rem;
//   color: #333;
//   font-family: 'Garamond', sans-serif;
// `;

// const Separator = styled.hr`
//   border: none;
//   height: 1px;
//   background: linear-gradient(to right, transparent, #13182a, transparent);
//   margin: 2rem 0;
// `;

const QuoteList = styled.div`
  position: relative;
  min-height: 100px; /* Adjust based on quote height */
  margin-top: 0rem;
`;

const QuoteItem = styled.div<{ isVisible: boolean }>`
  position: absolute;
  width: 100%;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 1.2s ease-in-out;
  background: radial-gradient(
    circle,
    rgba(19, 24, 42, 1) 0%,
    rgba(19, 24, 42, 0.85) 100%
  ); // 19, 24, 42 is #13182a
  //background: linear-gradient(120deg, rgba(19, 24, 42, 1), rgba(19, 24, 42, 0.85));
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.25);
`;

const QuoteText = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  text-align: center;
  color: #f0f0f0;
  font-family: 'Garamond', serif;
  font-style: italic;
  margin-bottom: 0.5rem;
`;

const QuoteAuthor = styled.p`
  font-size: 1rem;
  color: #f0f0f0;
  font-family: 'Garamond', serif;
  text-align: center;
`;

const SubHeroTitle = styled.h1`
  font-size: clamp(1.2rem, 5vw, 2rem);
  font-weight: 500;
  font-family: 'Garamond', serif;
  background-color: #13182a;
  border-radius: 4px;
  padding: 1rem;
  text-align: center;
  color: #f0f0f0;
`;

const PhilosophyPage: React.FC = () => {
  // Load Inter font
  useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Heaviside | Philosophy</title>
        <meta
          name="description"
          content="Heaviside is a next-generation quantitative hedge fund, founded to trade global markets at the highest level."
        />
        <meta
          name="keywords"
          content="Heaviside, quantitative finance, hedge fund, trading, AI-driven models"
        />
        <meta name="author" content="Heaviside Team" />
        <meta property="og:title" content="Heaviside | Philosophy" />
        <meta property="og:description" content="Explore the philosophy guiding Heaviside, the next-gen quant hedge fund challenging legacy institutions." />
        <meta property="og:url" content="https://hvside.com" />
        <meta property="og:image" content="https://hvside.com/logo_text_svg.svg" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Heaviside",
            "url": "https://hvside.com/philosophy",
            "logo": "https://hvside.com/logo_text_svg.svg",
            "description": "This page explains the founding principles, vision, and mission of Heaviside, a quantitative hedge fund.",
          }
          `}
          </script>
      </Helmet>
      <AboutContainer>
        <SubHero title="Philosophy" />

        <SectionWithVisibility>
          <SubHeroTitle>
            Heaviside is a next-generation quantitative hedge fund, founded with
            the singular ambition of trading global financial markets at the
            highest level.
          </SubHeroTitle>
        </SectionWithVisibility>

         {/* <SectionWithVisibility>
          <SectionTitle>Founding Team</SectionTitle>
          <Paragraph>
            The two founders of Heaviside are computer scientists who met at
            EPFL during the first year of their bachelor&apos;s degree.
          </Paragraph>
          <Paragraph>
            One began working in quantitative finance at the age of 19 and
            crossed the ocean to NYC to complete a master&apos;s degree at
            Carnegie Mellon University in computational finance. He then worked
            as a quantitative researcher at renowned hedge funds in NYC and
            Miami.
          </Paragraph>
          <Paragraph>
            The other pursued a master&apos;s degree in computer science at ETH
            Zürich, specializing in artificial intelligence. He then began a PhD
            in Quantitative Finance at the National University of Singapore,
            which he eventually left to start a tech company with Y Combinator.
          </Paragraph>
          <Paragraph>
            In 2024, after eight years of shaping their skills and building
            their friendship, they left everything behind to pursue the mission
            of their lives: Heaviside.
          </Paragraph>
        </SectionWithVisibility>

        <Separator />

        <SectionWithVisibility>
          <SectionTitle>Vision</SectionTitle>
          <Paragraph>
            Heaviside aims to become a global multi-strategy hedge fund, able to research, validate, and deploy ideas across asset classes. We want to pair the standards of leading managers with the agility of a focused, tech-driven team.
          </Paragraph>
          <Paragraph>
            As we grow, our aim is to build a lasting institution: a platform for systematic trading that attracts talent, protects capital, and thrives through changing markets.
          </Paragraph>
        </SectionWithVisibility>

        <Separator />

        <SectionWithVisibility>
          <SectionTitle>Mission</SectionTitle>
          <Paragraph>
            Heaviside&apos;s goal is to design systematic strategies that are reliable, adaptive, and ready to scale. We focus on solid research, clean infrastructure, and precise execution to generate steady, risk-controlled returns.
          </Paragraph>
          <Paragraph>
            Above all, we prioritize survival and consistency. Because in quantitative trading, endurance is the ultimate measure of success.
          </Paragraph>
        </SectionWithVisibility>

        <Separator />

        <SectionWithVisibility>
          <SectionTitle>Approach</SectionTitle>
          <Paragraph>
            We build every step of the process in-house, from data pipelines to research tools, portfolio models, and execution. Clean data, thoughtful models, and robust systems are the foundation of everything we trade.
          </Paragraph>
          <Paragraph>
            Above all, we value brilliance and bravery: free-minded builders who challenge entrenched firms, craft systems with their own hands, and refine them relentlessly. Keeping research and engineering close allows us to scale without losing clarity.
          </Paragraph>
        </SectionWithVisibility>

        <Separator />

        <SectionWithVisibility>
          <SectionTitle>Values</SectionTitle>
          <Paragraph>
            We work with integrity and align interests with partners and investors. High standards in research, technology, and risk management guide every decision.
          </Paragraph>
          <Paragraph>
            We stay curious, adopt new tools, and improve constantly. Resilience and collaboration are at the heart of how we build, steady systems, steady people, and relationships built to last.
          </Paragraph>
        </SectionWithVisibility>

        <Separator /> */}

        <SectionWithVisibility>
          <QuotesDisplay />
        </SectionWithVisibility>
      </AboutContainer>
    </>
  );
};

// Wrapper component to handle visibility for fade-in animation
const SectionWithVisibility: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Section ref={ref} isVisible={inView}>
      {children}
    </Section>
  );
};

// Component to handle the fading quotes
const QuotesDisplay: React.FC = () => {
  const quotes = [
    {
      id: 'quote1',
      text: 'God does not play dice.',
      author: 'Albert Einstein',
    },
    {
      id: 'quote2',
      text: 'Be careful what you wish for.',
      author: "Founder's former supervisor",
    },
    {
      id: 'quote3',
      text: 'No such thing as a free lunch.',
      author: 'American people',
    },
    {
      id: 'quote4',
      text: 'You should do things THE RIGHT WAY.',
      author: "Founder's former supervisor",
    },
    {
      id: 'quote5',
      text: 'I hope you kids are having fun.',
      author: "Founder's mother",
    },
    {
      id: 'quote6',
      text: "It's the way it is.",
      author: "Founder's former supervisor",
    },
    { id: 'quote7', text: "Don't die.", author: 'YC' },
  ];

  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
    }, 5000); // Change quote every 5 seconds

    return () => clearInterval(interval);
  }, [quotes.length]);

  return (
    <QuoteList>
      {quotes.map((quote, index) => (
        <QuoteItem key={quote.id} isVisible={index === currentQuoteIndex}>
          <QuoteText>&quot;{quote.text}&quot;</QuoteText>
          <QuoteAuthor>— {quote.author}</QuoteAuthor>
        </QuoteItem>
      ))}
    </QuoteList>
  );
};

export default PhilosophyPage;
